import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { QUOTE_SUBMIT_GUARD_MS } from "@/modules/dashboard/crypto-swaps/constants/cryptoSwaps.constants";
import { useOtcOrderPoller } from "@/modules/dashboard/crypto-swaps/hooks/useOtcOrderPoller";
import { useOtcQuote } from "@/modules/dashboard/crypto-swaps/hooks/useOtcQuote";
import type { UseSourceWalletBalanceResult } from "@/modules/dashboard/crypto-swaps/hooks/useSourceWalletBalance";
import { CryptoSwapsService } from "@/modules/dashboard/crypto-swaps/services/cryptoSwaps.service";
import type {
  CreateOtcQuotePayload,
  OtcQuote,
} from "@/modules/dashboard/crypto-swaps/types/cryptoSwaps.types";
import { boundsOf } from "@/modules/dashboard/crypto-swaps/utils/cryptoSwapLimits";
import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import { readApiCode, readApiMessages, readApiStatus } from "@/shared/utils/apiError";
import { compareDecimalStrings, isDecimalString } from "@/shared/utils/decimal";
import { isTwoFactorCodeError } from "@/shared/utils/twoFactorErrors";

import { CardCopTopUpsService } from "../services/cardCopTopUps.service";
import type { CardCopTopUp, CardCopTopUpTerms } from "../types/cardCopTopUp.types";
import {
  copTopUpRequest,
  creditedFor,
  isCopTopUpLive,
  type CopTopUpAnchor,
  type CopTopUpIssue,
} from "../utils/cardCopTopUp";
import {
  followedCopTopUp,
  freshCopTopUpAttempt,
  settleFreshFailure,
  settleLookupFailure,
  unresolvedCopTopUps,
  type CopTopUpAttempt,
} from "../utils/copTopUpAttempt";

const PAIR = "COP-USDT";

export type CopTopUpStep = "form" | "review" | "result";

export type CopTopUpSubmitError = { kind: "unconfirmed" } | { kind: "message"; messages: string[] };

export interface UseCardCopTopUpInput {
  cardId: string;
  terms: CardCopTopUpTerms;
  organizationId: string | null;
  balance: UseSourceWalletBalanceResult;
}

const priceQuote = (payload: CreateOtcQuotePayload, signal: AbortSignal): Promise<OtcQuote> =>
  CryptoSwapsService.createQuote(payload, signal);

const isLive = (topUp: CardCopTopUp) => isCopTopUpLive(topUp.status);

export function useCardCopTopUp({ cardId, terms, organizationId, balance }: UseCardCopTopUpInput) {
  const percent = terms.deposit_fee_percent ?? "0";
  const sendBounds = useMemo(() => boundsOf(terms.send_limits), [terms.send_limits]);
  const receiveBounds = useMemo(() => boundsOf(terms.receive_limits), [terms.receive_limits]);

  const [unresolved, setUnresolvedState] = useState<CopTopUpAttempt | null>(() =>
    unresolvedCopTopUps.get(cardId),
  );
  const [anchor, setAnchor] = useState<CopTopUpAnchor>("send");
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState<CopTopUpStep>(() => (unresolved ? "review" : "form"));
  const [topUpId, setTopUpId] = useState<string | null>(null);
  const [created, setCreated] = useState<CardCopTopUp | null>(null);
  const [refusal, setRefusal] = useState<string[] | null>(null);
  const [expiredQuoteId, setExpiredQuoteId] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [checkOutcome, setCheckOutcome] = useState<"pending" | "not_placed" | null>(null);

  const pricing = useOtcQuote(priceQuote);
  // Placing the load buys USDT on the desk before it answers: the dialog shows the money on its way instead
  // of leaving the challenge on "Verifying…".
  const twoFactor = useTwoFactorAction<CardCopTopUp>({ closeOnSend: true });

  const setUnresolved = useCallback(
    (attempt: CopTopUpAttempt | null) => {
      unresolvedCopTopUps.set(cardId, attempt);
      setUnresolvedState(attempt);
    },
    [cardId],
  );

  const request = useMemo(
    () => copTopUpRequest({ anchor, amount, percent, sendBounds, receiveBounds }),
    [anchor, amount, percent, sendBounds, receiveBounds],
  );
  const signature = `${anchor}:${amount.trim()}`;
  const payloadRef = useRef<CreateOtcQuotePayload | null>(null);

  const { requestQuote, clearQuote, clockOffsetMs } = pricing;
  const requestedQuote = request.quote;

  useEffect(() => {
    if (step !== "form") return;
    if (!requestedQuote) {
      payloadRef.current = null;
      clearQuote();
      return;
    }
    const payload: CreateOtcQuotePayload = {
      pair: PAIR,
      network: terms.network,
      ...requestedQuote,
      ...(organizationId ? { organization_id: organizationId } : {}),
    };
    payloadRef.current = payload;
    requestQuote(payload, { signature });
  }, [signature, requestedQuote, step, terms.network, organizationId, requestQuote, clearQuote]);

  const liveQuote = pricing.quoteSignature === signature ? pricing.quote : null;
  const quote = unresolved?.quote ?? liveQuote;
  const isExpired = !unresolved && !!liveQuote && expiredQuoteId === liveQuote.id;

  // A price dies shortly before the desk's lock: the form re-prices on its own, the review asks first.
  useEffect(() => {
    if (!liveQuote) return;
    const deadline = Date.parse(liveQuote.expires_at) - clockOffsetMs - QUOTE_SUBMIT_GUARD_MS;
    const timer = window.setTimeout(
      () => {
        if (step === "form" && payloadRef.current) {
          requestQuote(payloadRef.current, { signature, immediate: true });
        } else {
          setExpiredQuoteId(liveQuote.id);
        }
      },
      Math.max(0, deadline - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [liveQuote, step, clockOffsetMs, requestQuote, signature]);

  const delivered = quote?.receive.amount ?? null;
  const credited = delivered ? creditedFor(delivered, percent) : null;
  const debit = quote?.send.amount ?? null;

  const issue: CopTopUpIssue | null = useMemo(() => {
    if (request.issue) return request.issue;
    if (
      debit &&
      balance.available &&
      isDecimalString(balance.available) &&
      compareDecimalStrings(debit, balance.available) > 0
    ) {
      return { kind: "balance" };
    }
    return null;
  }, [request.issue, debit, balance.available]);

  const canReview = step === "form" && !!quote && !issue && !pricing.isPricing && !balance.isLoading;

  const editAmount = useCallback((side: CopTopUpAnchor, value: string) => {
    setAnchor(side);
    setAmount(value);
    setRefusal(null);
    setCheckOutcome(null);
  }, []);

  const openReview = useCallback(() => {
    if (canReview) setStep("review");
  }, [canReview]);

  const backToForm = useCallback(() => {
    if (unresolved) return;
    setRefusal(null);
    setStep("form");
  }, [unresolved]);

  const refreshPrice = useCallback(() => {
    if (unresolved || !payloadRef.current) return;
    setRefusal(null);
    requestQuote(payloadRef.current, { signature, immediate: true });
  }, [unresolved, requestQuote, signature]);

  const showPlaced = useCallback((placed: CardCopTopUp) => {
    setCreated(placed);
    setTopUpId(placed.id);
    setStep("result");
  }, []);

  const submit = useCallback(async () => {
    if (unresolved) return;
    const attempt = freshCopTopUpAttempt(liveQuote, pricing.idempotencyKey, isExpired);
    if (!attempt) return;
    setRefusal(null);
    setCheckOutcome(null);
    const sent: { request: Promise<CardCopTopUp> | null } = { request: null };
    const settle = (error: unknown) => {
      if (isTwoFactorCodeError(error)) return setUnresolved(null);
      const failure = settleFreshFailure(attempt, readApiStatus(error), readApiMessages(error));
      setUnresolved(failure.unresolved);
      if (!failure.unresolved) setRefusal(failure.messages);
    };
    try {
      const result = await twoFactor.start((code) => {
        unresolvedCopTopUps.set(cardId, attempt);
        sent.request = CardCopTopUpsService.createOnce(cardId, attempt.payload, code);
        return sent.request;
      });
      if (result) {
        setUnresolved(null);
        showPlaced(result);
        return;
      }
      if (!sent.request) return setUnresolved(null);
      // The challenge can close while a request is out: follow that request, never forget it.
      const late = await sent.request;
      setUnresolved(null);
      showPlaced(late);
    } catch (error: unknown) {
      settle(error);
    }
  }, [
    cardId,
    isExpired,
    liveQuote,
    pricing.idempotencyKey,
    setUnresolved,
    showPlaced,
    twoFactor,
    unresolved,
  ]);

  const check = useCallback(async () => {
    if (!unresolved || isChecking) return;
    setIsChecking(true);
    setCheckOutcome(null);
    try {
      const placed = await CardCopTopUpsService.findByKey(cardId, unresolved.payload);
      setUnresolved(null);
      showPlaced(placed);
    } catch (error: unknown) {
      if (settleLookupFailure(readApiStatus(error), readApiCode(error)) === "not_placed") {
        setUnresolved(null);
        setStep("form");
        setCheckOutcome("not_placed");
      } else {
        setCheckOutcome("pending");
      }
    } finally {
      setIsChecking(false);
    }
  }, [cardId, isChecking, setUnresolved, showPlaced, unresolved]);

  // An attempt that outlived a reload is resolved as soon as the dialog opens on it.
  const restoredRef = useRef(unresolved !== null);
  useEffect(() => {
    if (!restoredRef.current) return;
    restoredRef.current = false;
    void check();
  }, [check]);

  const fetchTopUp = useCallback((id: string) => CardCopTopUpsService.get(cardId, id), [cardId]);
  const followed = useOtcOrderPoller(topUpId, fetchTopUp, isLive);
  const vanished = followed.notFound;
  const topUp = followedCopTopUp(followed, created);

  const startOver = useCallback(() => {
    setTopUpId(null);
    setCreated(null);
    setStep("form");
    setCheckOutcome("not_placed");
  }, []);

  const submitError: CopTopUpSubmitError | null = unresolved
    ? { kind: "unconfirmed" }
    : refusal
      ? { kind: "message", messages: refusal }
      : null;

  return {
    step,
    anchor,
    amount,
    editAmount,
    percent,
    sendBounds,
    quote,
    isPricing: pricing.isPricing,
    quoteError: pricing.error,
    clockOffsetMs,
    delivered,
    credited,
    debit,
    issue,
    balance,
    canReview,
    openReview,
    backToForm,
    isExpired,
    isUnresolved: !!unresolved,
    check,
    isChecking,
    checkOutcome,
    vanished,
    startOver,
    refreshPrice,
    submit,
    isSubmitting: twoFactor.isRunning,
    submitError,
    twoFactor,
    topUp,
  };
}

export type UseCardCopTopUpResult = ReturnType<typeof useCardCopTopUp>;
